import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { DsChipColorVariant } from '@levelaccess/design-system';
import { VariationValue } from '@harnessio/ff-javascript-client-sdk';
import { switchMap } from 'rxjs/operators';
import { IScanIssues, IScanIssuesSuccessCriteria } from '../../../../../../../../shared/interfaces/scan-issues.interface';
import { ISuccessCriteria } from '../../../../../../../../shared/audits/definitions/success-criteria/success-criteria.interface';
import {
  $auditStandard,
  AuditStandards,
  mapAuditDbStandardToAuditStandard,
} from '../../../../../../../../shared/constants/audit-standard';
import { $scanIssues } from '../../../../../../../../shared/constants/scan-issues';
import { AccessibilityAuditToolNames } from '../../../../../../../../shared/constants/audit-tool';
import { $auditStandardSuccessCriteria } from '../../../../../../../../shared/constants/audit-standard-success-criteria';
import { SuccessCriteriaService } from '../../../../../../services/success-criteria.service';
import { TenantSeveritiesService } from '../../../../../../services/tenant-severities.service';
import { ICustomSeveritiesMap, ICustomSeverity } from '../../../../../../../../shared/interfaces/tenant.interface';
import { FeatureFlagService } from '../../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../../shared/interfaces/feature-flag.interface';
import { $successCriteria } from '../../../../../../../../shared/audits/definitions/success-criteria/constants';

@Component({
  selector: 'app-issues-view-rule-info',
  templateUrl: './issues-view-rule-info.component.html',
  styleUrls: ['./issues-view-rule-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IssuesViewRuleInfoComponent implements OnInit, OnDestroy {
  @Input()
  public scanIssues: IScanIssues;

  public $scanIssues: typeof $scanIssues = $scanIssues;
  public DsChipColorVariant: typeof DsChipColorVariant = DsChipColorVariant;
  public AccessibilityAuditToolNames: typeof AccessibilityAuditToolNames = AccessibilityAuditToolNames;

  private subscription: Subscription;
  protected customSeverity$: BehaviorSubject<ICustomSeverity>;
  protected readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;

  public isNonWcagStandardsAutomated$: Observable<VariationValue>;

  constructor(
    private successCriteriaService: SuccessCriteriaService,
    private tenantSeveritiesService: TenantSeveritiesService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.subscription = new Subscription();
    this.customSeverity$ = new BehaviorSubject<ICustomSeverity>(null);
  }

  public get successCriteria(): ISuccessCriteria[] {
    return this.scanIssues[$scanIssues.successCriteria].map((sc: IScanIssuesSuccessCriteria) => {
      const auditStandard = sc[$auditStandardSuccessCriteria.auditStandards][0];
      const standard = mapAuditDbStandardToAuditStandard[auditStandard[$auditStandard.standard]];
      const version = auditStandard[$auditStandard.version] as ISuccessCriteria['versions'][0];

      const successCriteriaRecord: ISuccessCriteria | null = this.successCriteriaService.getSuccessCriteriaFromStandard(
        standard ?? AuditStandards.wcag,
        sc[$auditStandard.identifier],
      );

      if (successCriteriaRecord) {
        successCriteriaRecord[$successCriteria.versions] = [version];
        successCriteriaRecord[$successCriteria.standard] = standard;
      }

      return successCriteriaRecord;
    });
  }

  public ngOnInit(): void {
    this.isNonWcagStandardsAutomated$ = this.featureFlagService.variation$(
      FeatureFlagCollection.non_wcag_standards_automated,
      false,
    );

    this.subscription.add(
      this.isNonWcagStandardsAutomated$.pipe(switchMap(() => this.tenantSeveritiesService.getAll())).subscribe({
        next: (customSeverities: ICustomSeveritiesMap) => {
          this.customSeverity$.next(customSeverities?.get(this.scanIssues[$scanIssues.severity]));
        },
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
